import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Typography,
  CircularProgress,
  AvatarGroup,
  Avatar,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { useNavigate } from 'react-router'

import { useUsers } from '../../contexts/UserContext'
import TripService from '../../services/TripService'
import { avatarUrl } from '../../utils/gravatar'
import useBar from '../common/Bar'

export default function TripTable() {
  const navigate = useNavigate()
  const { fetchUser } = useUsers()
  const bar = useBar()
  const [isLoading, setIsLoading] = useState(false)
  const [trips, setTrips] = useState([])

  // Table state
  const [orderBy, setOrderBy] = useState('date')
  const [order, setOrder] = useState('desc')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedTrip, setSelectedTrip] = useState(null)
  const [participants, setParticipants] = useState({})

  // Get all trips
  const fetchTrips = async () => {
    setIsLoading(true)
    try {
      const filters = {
        size: 999,
      }
      const response = await TripService.getTrips(filters)
      setTrips(response)
    } catch (error) {
      console.error('Failed to fetch trips:', error)
      bar.error(error.message || 'Failed to fetch trips')
    } finally {
      setIsLoading(false)
    }
  }

  // 初始化加载数据
  React.useEffect(() => {
    fetchTrips()
  }, [])

  // Handle sort
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  // Sort function
  const sortData = (data) => {
    if (data === undefined || data.length === 0) return []

    return data.sort((a, b) => {
      let aValue = a[orderBy]
      let bValue = b[orderBy]

      // Special handling for dates
      if (orderBy === 'date') {
        aValue = dayjs(a.date)
        bValue = dayjs(b.date)
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : 1
      } else {
        return bValue < aValue ? -1 : 1
      }
    })
  }

  // Filter function
  const filterData = (data) => {
    if (data === undefined) return []

    return data.filter((trip) => {
      const matchesStatus = statusFilter === 'all' || trip.status === statusFilter
      const searchTerms = searchQuery.toLowerCase().trim().split(/\s+/)

      const matchesSearch = searchTerms.every((term) => {
        return trip.title?.toLowerCase().includes(term) || trip.skiResort.toLowerCase().includes(term) || trip.status.toLowerCase().includes(term)
      })

      return matchesStatus && matchesSearch
    })
  }

  // Load participants for a trip
  const loadParticipants = async (tripId, participantIds) => {
    if (!participantIds?.length || participants[tripId]) return

    try {
      const participantsData = await Promise.all(participantIds.map((id) => fetchUser({ userId: id })))
      setParticipants((prev) => ({
        ...prev,
        [tripId]: participantsData.map((participant) => ({
          id: participant.resourceId,
          name: `${participant.firstName} ${participant.lastName}`,
          email: participant.email,
          avatar: participant.avatar,
        })),
      }))
    } catch (error) {
      console.error('Error loading participants:', error)
    }
  }

  // Handle delete dialog
  const handleDeleteClick = (trip) => {
    setSelectedTrip(trip)
    setDeleteDialogOpen(true)
  }

  // Handle trip deletion
  const handleDelete = async () => {
    if (!selectedTrip) return

    try {
      await TripService.deleteTrip(selectedTrip.resourceId)
      bar.success('Trip deleted successfully')
      setDeleteDialogOpen(false)
      await fetchTrips() // Reload data
    } catch (error) {
      console.error('Failed to delete trip:', error)
      bar.error(error.message || 'Failed to delete trip')
    } finally {
      setSelectedTrip(null)
    }
  }

  // Process data with sorting and filtering
  const processedData = sortData(filterData(trips))

  const handleUserClick = (userId) => {
    navigate(`/users/${userId}`)
  }

  const handleEditClick = (trip) => {
    navigate(`/events/trips/${trip.resourceId}/edit`)
  }

  const handleViewClick = (trip) => {
    navigate(`/events/trips/${trip.resourceId}`)
  }

  // Load participants for all trips
  React.useEffect(() => {
    const loadAllParticipants = async () => {
      const tripsWithParticipants = processedData.filter((trip) => trip.participantIds?.length > 0)
      for (const trip of tripsWithParticipants) {
        if (!participants[trip.resourceId]) {
          await loadParticipants(trip.resourceId, trip.participantIds)
        }
      }
    }
    loadAllParticipants()
  }, [processedData])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          placeholder="Search by title, location, or status"
          helperText="Multiple keywords supported (space separated)"
        />
        <FormControl size="small" sx={{ width: 150 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status Filter">
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="PUBLISHED">Published</MenuItem>
            <MenuItem value="CANCELLED">Cancelled</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: 'fixed' }} size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>
                <TableSortLabel active={orderBy === 'title'} direction={orderBy === 'title' ? order : 'asc'} onClick={() => handleRequestSort('title')}>
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '15%' }}>
                <TableSortLabel active={orderBy === 'date'} direction={orderBy === 'date' ? order : 'asc'} onClick={() => handleRequestSort('date')}>
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '15%' }}>
                <TableSortLabel active={orderBy === 'skiResort'} direction={orderBy === 'skiResort' ? order : 'asc'} onClick={() => handleRequestSort('skiResort')}>
                  Location
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '15%' }}>Participants</TableCell>
              <TableCell sx={{ width: '15%' }}>Registration Deadline</TableCell>
              <TableCell sx={{ width: '10%' }}>
                <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleRequestSort('status')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '10%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.map((trip) => (
              <TableRow key={trip.resourceId} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{trip.title}</TableCell>
                <TableCell>{dayjs(trip.date).format('YYYY-MM-DD')}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{trip.skiResort}</TableCell>
                <TableCell>
                  {trip.participantIds?.length > 0 ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                        {participants[trip.resourceId]?.map((participant) => (
                          <Tooltip key={participant.id} title={participant.name}>
                            <Avatar src={avatarUrl(participant)} sx={{ width: 24, height: 24 }}>
                              {participant.name?.[0]}
                            </Avatar>
                          </Tooltip>
                        ))}
                      </AvatarGroup>
                      <Typography variant="body2" color="text.secondary">
                        {trip.participantIds.length}/{trip.maxParticipants || '∞'}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      0/{trip.maxParticipants || '∞'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{trip.registrationDeadline ? dayjs(trip.registrationDeadline).format('YYYY-MM-DD') : 'No deadline'}</TableCell>
                <TableCell>
                  <Chip
                    label={trip.status}
                    size="small"
                    color={
                      trip.status === 'PUBLISHED'
                        ? 'primary'
                        : trip.status === 'COMPLETED'
                          ? 'success'
                          : trip.status === 'CANCELLED'
                            ? 'error'
                            : trip.status === 'DRAFT'
                              ? 'default'
                              : 'default'
                    }
                    sx={{ height: 20 }}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewClick(trip)} sx={{ p: 0.5 }}>
                        <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Trip">
                      <IconButton size="small" onClick={() => handleEditClick(trip)} sx={{ p: 0.5 }}>
                        <EditIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Trip">
                      <IconButton size="small" onClick={() => handleDeleteClick(trip)} sx={{ p: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        disablePortal={false}
        keepMounted
      >
        <DialogTitle id="delete-dialog-title">Delete Trip</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">Are you sure you want to delete this trip? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" autoFocus>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
